import { useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, Building2, Clock3, Download, Headphones, LogIn, LogOut, Megaphone, PhoneOff, Pencil, Printer, RefreshCw, Save, Users } from "lucide-react";
import { api, apiErrToStr, fmtDuration, translateAbandonmentCause, translateHangupCause } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const realtimeInitial = {
  summary: {},
  queues: [],
  agents: [],
  hourly: [],
  abandonment_causes: [],
  disconnect_causes: [],
};

const reportsInitial = { summary: {}, queues: [], agents: [] };

function Metric({ label, value, detail, icon: Icon, tone = "blue" }) {
  const colors = {
    blue: "text-[color:var(--accent-blue)]",
    green: "text-[color:var(--accent-green)]",
    amber: "text-[color:var(--accent-amber)]",
    red: "text-[color:var(--accent-red)]",
  };

  return (
    <div className="sbc-card p-3 flex items-center gap-2.5">
      <div className={`w-8 h-8 flex items-center justify-center bg-[color:var(--bg-elevated)] ${colors[tone]}`}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className={`text-xl leading-none font-mono font-semibold ${colors[tone]}`}>{value}</div>
        <div className="text-[11px] text-[color:var(--text-muted)]">{detail}</div>
      </div>
    </div>
  );
}

function stateLabel(value) {
  return (
    {
      available: "Disponivel",
      busy: "Em atendimento",
      on_break: "Em pausa",
      offline: "Offline",
    }[value] || value || "Offline"
  );
}

function queueDiagnosisTone(level) {
  if (level === "critical") return "tag-danger";
  if (level === "warning") return "tag-warning";
  return "tag-online";
}

export default function SupervisorPanel() {
  const { user, tenants = [], activeTenantId, setActiveTenantId, reloadTenants } = useAuth();
  const [data, setData] = useState(realtimeInitial);
  const [reports, setReports] = useState(reportsInitial);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(true);
  const [days, setDays] = useState(1);
  const [editingAgentId, setEditingAgentId] = useState("");
  const [editingQueues, setEditingQueues] = useState([]);
  const [editingExtension, setEditingExtension] = useState("");
  const [queueActionBusy, setQueueActionBusy] = useState(false);
  const [queueActionError, setQueueActionError] = useState("");
  const [queueActionSuccess, setQueueActionSuccess] = useState("");
  const [hangingUpSession, setHangingUpSession] = useState("");
  const [broadcast, setBroadcast] = useState({ mensagem: "", ativo: false, updated_at: null });
  const [broadcastDirty, setBroadcastDirty] = useState(false);
  const [broadcastEditing, setBroadcastEditing] = useState(false);

  const loadRealtime = async ({ withLoading = false } = {}) => {
    if (withLoading) setLoading(true);
    try {
      const response = await api.get("/callcenter/supervisor", {
        params: { days, service_level_seconds: 20, _ts: Date.now() },
      });
      setData((current) => ({ ...current, ...(response.data || realtimeInitial) }));
      if (!broadcastDirty) {
        setBroadcast({
          mensagem: response.data?.broadcast?.mensagem || "",
          ativo: Boolean(response.data?.broadcast?.ativo),
          updated_at: response.data?.broadcast?.updated_at || null,
        });
      }
      setConnected(true);
    } catch (_err) {
      setConnected(false);
    } finally {
      if (withLoading) setLoading(false);
    }
  };

  const loadReports = async () => {
    const reportResponse = await api.get("/callcenter/reports", {
      params: { days, service_level_seconds: 20, _ts: Date.now() },
    });
    setReports(reportResponse.data || reportsInitial);
  };

  const load = async (manual = false) => {
    if (manual) setLoading(true);
    try {
      await Promise.all([
        loadRealtime({ withLoading: false }),
        loadReports(),
      ]);
    } finally {
      if (manual) setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    const timer = setInterval(() => {
      if (document.hidden) return;
      if (broadcastEditing || broadcastDirty || editingAgentId) return;
      loadRealtime({ withLoading: false });
    }, 5000);
    return () => clearInterval(timer);
  }, [days, broadcastEditing, broadcastDirty, editingAgentId]);

  const summary = data.summary || {};
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "superadmin";
  const activeTenantLabel = (tenants || []).find((tenant) => String(tenant.id) === String(activeTenantId || ""))?.nome || "";
  const maxHour = Math.max(1, ...(data.hourly || []).map((row) => Number(row.offered || 0)));
  const topHours = useMemo(
    () => (data.hourly || []).filter((row) => Number(row.offered || 0) > 0).slice(0, 12),
    [data.hourly],
  );
  const editingAgent = (data.agents || []).find((agent) => agent.id === editingAgentId) || null;

  const startQueueEdit = (agent) => {
    setEditingAgentId(agent.id);
    setEditingQueues(Array.isArray(agent.filas) ? agent.filas.map(String) : []);
    setEditingExtension(String(agent.active_extension || agent.ramal || ""));
    setQueueActionError("");
    setQueueActionSuccess("");
  };

  const toggleQueue = (queueNumber) => {
    const normalized = String(queueNumber || "");
    setEditingQueues((current) =>
      current.includes(normalized) ? current.filter((item) => item !== normalized) : [...current, normalized],
    );
  };

  const saveAgentQueues = async () => {
    if (!editingAgentId) return;
    setQueueActionBusy(true);
    setQueueActionError("");
    setQueueActionSuccess("");
    try {
      await api.put(`/callcenter/supervisor/agents/${editingAgentId}/queues`, { filas: editingQueues });
      setQueueActionSuccess("Filas do agente atualizadas.");
      await loadRealtime({ withLoading: true });
    } catch (err) {
      setQueueActionError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setQueueActionBusy(false);
    }
  };

  const saveAgentExtension = async () => {
    if (!editingAgentId || !editingExtension) return;
    setQueueActionBusy(true);
    setQueueActionError("");
    setQueueActionSuccess("");
    try {
      await api.put(`/callcenter/agents/${editingAgentId}/extension`, { extension: editingExtension });
      setQueueActionSuccess(`Ramal do agente atualizado para ${editingExtension}.`);
      await loadRealtime({ withLoading: true });
    } catch (err) {
      setQueueActionError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setQueueActionBusy(false);
    }
  };

  const setAgentLoginState = async (agent, shouldLogin) => {
    if (!agent?.id) return;
    setQueueActionBusy(true);
    setQueueActionError("");
    setQueueActionSuccess("");
    try {
      await api.post(`/callcenter/agents/${agent.id}/${shouldLogin ? "login" : "logout"}`);
      setQueueActionSuccess(
        shouldLogin
          ? `Agente ${agent.nome || agent.agente} logado com sucesso.`
          : `Agente ${agent.nome || agent.agente} deslogado com sucesso.`,
      );
      await loadRealtime({ withLoading: false });
    } catch (err) {
      setQueueActionError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setQueueActionBusy(false);
    }
  };

  const cancelQueueEdit = () => {
    setEditingAgentId("");
    setEditingQueues([]);
    setEditingExtension("");
    setQueueActionError("");
    setQueueActionSuccess("");
  };

  const saveBroadcast = async () => {
    setQueueActionBusy(true);
    setQueueActionError("");
    setQueueActionSuccess("");
    try {
      const payload = {
        mensagem: broadcast.mensagem || "",
        ativo: Boolean(broadcast.ativo),
      };
      const { data: saved } = await api.put("/callcenter/supervisor/broadcast", payload);
      setBroadcast({
        mensagem: saved?.mensagem || "",
        ativo: Boolean(saved?.ativo),
        updated_at: saved?.updated_at || null,
      });
      setBroadcastDirty(false);
      setQueueActionSuccess(saved?.ativo ? "Letreiro publicado para os agentes." : "Letreiro salvo e desativado.");
    } catch (err) {
      setQueueActionError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setQueueActionBusy(false);
    }
  };

  const hangupWaitingCall = async (call) => {
    if (!call?.queue_number || !call?.session_uuid) return;
    if (!window.confirm(`Encerrar a chamada ${call.caller_number || "sem identificacao"} da fila ${call.queue_number}?`)) return;
    setHangingUpSession(call.session_uuid);
    setQueueActionError("");
    setQueueActionSuccess("");
    try {
      await api.post("/callcenter/supervisor/queue/hangup", {
        queue_number: call.queue_number,
        session_uuid: call.session_uuid,
      });
      setQueueActionSuccess(`Ligacao da fila ${call.queue_number} encerrada.`);
      await loadRealtime({ withLoading: false });
    } catch (err) {
      setQueueActionError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setHangingUpSession("");
    }
  };

  const downloadReport = () => {
    const rows = [
      ["RELATORIO CALL CENTER", `Periodo: ${days} dia(s)`],
      [],
      ["Resumo", "Valor"],
      ["Oferecidas", reports.summary?.offered || 0],
      ["Atendidas", reports.summary?.answered || 0],
      ["Abandonadas", reports.summary?.abandoned || 0],
      ["Nivel de servico", `${Math.round(reports.summary?.service_level || 0)}%`],
      ["Espera media", fmtDuration(reports.summary?.avg_wait_sec || 0)],
      [],
      ["Fila", "Oferecidas", "Atendidas", "Abandonadas", "SLA", "Espera media"],
      ...(reports.queues || []).map((row) => [
        row.queue,
        row.offered || 0,
        row.answered || 0,
        row.abandoned || 0,
        `${Math.round(row.service_level || 0)}%`,
        fmtDuration(row.avg_wait_sec || 0),
      ]),
      [],
      ["Agente", "Oferecidas", "Atendidas", "Abandonadas", "SLA", "Espera media"],
      ...(reports.agents || []).map((row) => [
        row.agent,
        row.offered || 0,
        row.answered || 0,
        row.abandoned || 0,
        `${Math.round(row.service_level || 0)}%`,
        fmtDuration(row.avg_wait_sec || 0),
      ]),
    ];
    const csv = rows
      .map((row) => row.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(";"))
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
    link.download = `relatorio-callcenter-${days}d.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-4" data-testid="supervisor-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Operacao em tempo real</div>
          <h1 className="text-xl font-semibold tracking-tight">Painel de Supervisao</h1>
          <p className="text-xs text-[color:var(--text-secondary)] mt-1">
            Acompanhamento das filas, agentes e relatorios do call center.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isSuperAdmin ? (
            <div className="flex items-center gap-2 rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] px-2 py-1.5">
              <Building2 size={14} className="text-[color:var(--text-muted)]" />
              <div className="min-w-[220px]">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Cliente</div>
                <select
                  className="w-full bg-transparent text-sm outline-none"
                  value={activeTenantId || ""}
                  onChange={(event) => setActiveTenantId(event.target.value)}
                >
                  {(tenants || []).map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.nome || tenant.name || tenant.id}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="sbc-btn" onClick={reloadTenants} title="Recarregar clientes">
                <RefreshCw size={13} />
              </button>
            </div>
          ) : activeTenantLabel ? (
            <div className="rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Cliente</div>
              <div className="text-sm font-medium">{activeTenantLabel}</div>
            </div>
          ) : null}
          <select className="sbc-input w-auto" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="1">Hoje</option>
            <option value="7">Ultimos 7 dias</option>
            <option value="30">Ultimos 30 dias</option>
          </select>
          <button className="sbc-btn" onClick={downloadReport} title="Exportar relatorio">
            <Download size={14} /> Excel
          </button>
          <button className="sbc-btn" onClick={() => window.print()} title="Imprimir ou salvar em PDF">
            <Printer size={14} /> PDF
          </button>
          <span className={`sbc-tag ${connected ? "tag-online" : "tag-danger"}`}>{connected ? "ONLINE" : "SEM CONEXAO"}</span>
          <button className="sbc-btn" onClick={() => load(true)} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
        <Metric label="Na fila" value={summary.waiting_calls || 0} detail="aguardando atendimento" icon={Clock3} tone={summary.waiting_calls ? "amber" : "green"} />
        <Metric label="Em atendimento" value={summary.active_calls || 0} detail="chamadas ativas" icon={Activity} tone="blue" />
        <Metric label="Agentes online" value={summary.available_agents || 0} detail={`${summary.agents || 0} cadastrados`} icon={Users} tone="green" />
        <Metric label="Atendidas" value={summary.answered || 0} detail={`${summary.offered || 0} oferecidas`} icon={Headphones} tone="green" />
        <Metric label="SLA" value={`${Math.round(summary.service_level || 0)}%`} detail="nivel de servico" icon={Activity} tone={(summary.service_level || 0) >= 80 ? "green" : "amber"} />
        <Metric label="Abandono" value={`${Math.round(summary.abandon_rate || 0)}%`} detail={`espera media ${fmtDuration(summary.avg_wait_sec || 0)}`} icon={AlertTriangle} tone={(summary.abandon_rate || 0) > 10 ? "red" : "amber"} />
      </div>

      <section className="sbc-card p-3 space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Megaphone size={16} />
              <h2 className="font-semibold">Letreiro de broadcast</h2>
            </div>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              A mensagem aparece rolando no topo do painel dos agentes deste cliente.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              checked={broadcast.ativo}
              onChange={(event) => {
                setBroadcastDirty(true);
                setBroadcast((current) => ({ ...current, ativo: event.target.checked }));
              }}
            />
            Ativar letreiro
          </label>
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px]">
          <textarea
            className="sbc-input min-h-[78px] resize-y"
            maxLength={280}
            placeholder="Ex.: Equipe, fila atendimento em alta demanda. Priorizem retorno imediato."
            value={broadcast.mensagem}
            onFocus={() => setBroadcastEditing(true)}
            onBlur={() => setBroadcastEditing(false)}
            onChange={(event) => {
              setBroadcastDirty(true);
              setBroadcast((current) => ({ ...current, mensagem: event.target.value.slice(0, 280) }));
            }}
          />
          <div className="flex flex-col gap-2">
            <button
              className="sbc-btn sbc-btn-primary justify-center"
              type="button"
              disabled={queueActionBusy || (!broadcast.mensagem.trim() && broadcast.ativo)}
              onClick={saveBroadcast}
            >
              {queueActionBusy ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Salvar letreiro
            </button>
            <button
              className="sbc-btn justify-center"
              type="button"
              disabled={queueActionBusy}
              onClick={() => {
                setBroadcastDirty(false);
                setBroadcast({ mensagem: "", ativo: false, updated_at: broadcast.updated_at });
              }}
            >
              Limpar
            </button>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 text-xs">
          <div className="text-[color:var(--text-muted)]">
            {broadcast.updated_at ? `Ultima atualizacao: ${new Date(broadcast.updated_at).toLocaleString("pt-BR")}` : "Nenhum letreiro publicado ainda."}
          </div>
          <div className="flex items-center gap-2">
            {(broadcastEditing || broadcastDirty || editingAgentId) ? (
              <span className="sbc-tag tag-warning">Autoatualizacao pausada durante a edicao</span>
            ) : null}
            <span className={`sbc-tag ${broadcast.ativo && broadcast.mensagem.trim() ? "tag-online" : "tag-offline"}`}>
              {broadcast.ativo && broadcast.mensagem.trim() ? "Ao vivo" : "Desativado"}
            </span>
          </div>
        </div>
        <div className="overflow-hidden border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)]">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)]">Pre-visualizacao</div>
          <div className="px-3 py-2.5 text-sm font-mono text-[color:var(--accent-blue)] voxyra-broadcast-track">
            <div className="voxyra-broadcast-marquee">
              {broadcast.mensagem.trim() || "Sua mensagem aparecera aqui no letreiro dos agentes."}
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,1fr)] gap-4">
        <section className="sbc-card overflow-hidden">
          <div className="p-3 border-b border-[color:var(--border-default)] flex justify-between">
            <h2 className="font-semibold">Filas em tempo real</h2>
            <span className="text-xs text-[color:var(--text-muted)]">Atualiza a cada 5s</span>
          </div>
          <div className="overflow-auto">
            <table className="sbc-table">
              <thead>
                <tr>
                  <th>Fila</th>
                  <th>Aguardando</th>
                  <th>Em atendimento</th>
                  <th>Atendidas</th>
                  <th>SLA</th>
                  <th>Agentes</th>
                  <th>Diagnostico</th>
                  <th>Espera media</th>
                </tr>
              </thead>
              <tbody>
                {(data.queues || []).length ? (
                  data.queues.map((queue) => (
                    <tr key={queue.id || queue.number}>
                      <td>
                        <strong>{queue.name || `Fila ${queue.number}`}</strong>
                        <div className="font-mono text-xs text-[color:var(--text-muted)]">{queue.number}</div>
                      </td>
                      <td>
                        <span className={`font-mono font-semibold ${queue.waiting ? "text-[color:var(--accent-amber)]" : "text-[color:var(--accent-green)]"}`}>
                          {queue.waiting || 0}
                        </span>
                      </td>
                      <td className="font-mono">{queue.active || 0}</td>
                      <td className="font-mono">{queue.answered || 0}</td>
                      <td className="font-mono">{Math.round(queue.service_level || 0)}%</td>
                      <td className="font-mono text-xs">
                        {queue.available_agents || 0} disp. / {queue.logged_agents || 0} log. / {queue.assigned_agents || 0} assoc.
                      </td>
                      <td>
                        <div className="space-y-1">
                          <span className={`sbc-tag ${queueDiagnosisTone(queue.delivery_diagnosis?.level)}`}>
                            {queue.delivery_diagnosis?.code || "ok"}
                          </span>
                          <div className="text-xs">{queue.delivery_diagnosis?.message || "Fila pronta"}</div>
                          <div className="text-[10px] text-[color:var(--text-muted)]">{queue.delivery_diagnosis?.hint || ""}</div>
                        </div>
                      </td>
                      <td className="font-mono">{fmtDuration(queue.avg_wait_sec || 0)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-[color:var(--text-muted)]">Nenhuma fila ativa.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="sbc-card p-3">
          <h2 className="font-semibold">Volume por horario</h2>
          <div className="h-48 mt-3 flex items-end gap-1 border-b border-[color:var(--border-default)]">
            {topHours.length ? (
              topHours.map((row) => (
                <div key={row.hour} className="flex-1 h-full flex flex-col items-center justify-end gap-1" title={`${row.hour}: ${row.offered || 0} chamadas`}>
                  <span className="text-[10px] font-mono">{row.offered || 0}</span>
                  <div className="w-full max-w-8 bg-[color:var(--accent-blue)] min-h-[2px]" style={{ height: `${Math.max(2, (Number(row.offered || 0) / maxHour) * 80)}%` }} />
                  <span className="text-[9px] text-[color:var(--text-muted)] mb-1">{row.hour.slice(0, 2)}h</span>
                </div>
              ))
            ) : (
              <div className="m-auto text-sm text-[color:var(--text-muted)]">Aguardando dados.</div>
            )}
          </div>
        </section>
      </div>

      <section className="sbc-card overflow-hidden">
        <div className="p-3 border-b border-[color:var(--border-default)] flex justify-between gap-3">
          <div>
            <h2 className="font-semibold">Ligacoes nas filas em tempo real</h2>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              Chamadas aguardando distribuicao ou atendimento nas filas deste cliente.
            </p>
          </div>
          <span className="sbc-tag tag-online">
            {(data.waiting_calls || []).length} ao vivo
          </span>
        </div>
        <div className="overflow-auto">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Fila</th>
                <th>Origem</th>
                <th>Nome</th>
                <th>Tempo na fila</th>
                <th>Estado</th>
                <th className="text-right">Acao</th>
              </tr>
            </thead>
            <tbody>
              {(data.waiting_calls || []).length ? (
                (data.waiting_calls || []).map((call) => (
                  <tr key={call.session_uuid || `${call.queue_number}-${call.caller_number}-${call.wait_sec}`}>
                    <td>
                      <strong>{call.queue_name || `Fila ${call.queue_number}`}</strong>
                      <div className="font-mono text-xs text-[color:var(--text-muted)]">{call.queue_number || "-"}</div>
                    </td>
                    <td className="font-mono">{call.caller_number || "-"}</td>
                    <td>{call.caller_name || "-"}</td>
                    <td className="font-mono">{fmtDuration(call.wait_sec || 0)}</td>
                    <td>{call.state || "Waiting"}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="sbc-btn sbc-btn-danger"
                        onClick={() => hangupWaitingCall(call)}
                        disabled={hangingUpSession === call.session_uuid}
                      >
                        {hangingUpSession === call.session_uuid ? <RefreshCw size={14} className="animate-spin" /> : <PhoneOff size={14} />}
                        Desligar
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-[color:var(--text-muted)]">Nenhuma ligacao aguardando nas filas agora.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {queueActionError ? (
          <div className="mx-3 mb-3 text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
            ! {queueActionError}
          </div>
        ) : null}
        {queueActionSuccess ? (
          <div className="mx-3 mb-3 text-xs text-[color:var(--accent-green)] font-mono border border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.08)] px-3 py-2 rounded-sm">
            {queueActionSuccess}
          </div>
        ) : null}
      </section>

      <section className="sbc-card overflow-hidden">
        <div className="p-3 border-b border-[color:var(--border-default)]">
          <h2 className="font-semibold">Agentes do grupo</h2>
        </div>
        <div className="overflow-auto">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Agente</th>
                <th>Login</th>
                <th>Ramal em uso</th>
                <th>Filas</th>
                <th>Estado</th>
                <th>Chamada</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {(data.agents || []).length ? (
                data.agents.map((agent) => (
                  <tr key={agent.id}>
                    <td>{agent.nome || agent.agente}</td>
                    <td className="font-mono">{agent.agente || "-"}</td>
                    <td className="font-mono">{agent.active_extension || agent.ramal || "-"}</td>
                    <td className="font-mono text-xs">{(agent.filas || []).join(", ") || "-"}</td>
                    <td>
                      <span className={`sbc-tag ${agent.status === "available" ? "tag-online" : agent.status === "busy" ? "tag-warning" : ""}`}>
                        {stateLabel(agent.status)}
                      </span>
                    </td>
                    <td className="text-xs text-[color:var(--text-muted)]">{agent.active_call_uuid ? "Em chamada" : "Livre"}</td>
                    <td>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="sbc-btn"
                          onClick={() => setAgentLoginState(agent, true)}
                          disabled={queueActionBusy || agent.status === "available" || agent.status === "busy"}
                        >
                          <LogIn size={14} /> Logar ramal
                        </button>
                        <button
                          type="button"
                          className="sbc-btn"
                          onClick={() => setAgentLoginState(agent, false)}
                          disabled={queueActionBusy || agent.status === "offline"}
                        >
                          <LogOut size={14} /> Deslogar ramal
                        </button>
                        <button type="button" className="sbc-btn" onClick={() => startQueueEdit(agent)}>
                          <Pencil size={14} /> Trocar filas
                        </button>
                        <button type="button" className="sbc-btn" onClick={() => startQueueEdit(agent)}>
                          <Pencil size={14} /> Trocar ramal
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-[color:var(--text-muted)]">Nenhum agente cadastrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {editingAgent ? (
          <div className="border-t border-[color:var(--border-default)] p-3 space-y-3 bg-[color:var(--bg-elevated)]">
            <div>
              <div className="text-sm font-semibold">Configuracoes do agente {editingAgent.nome || editingAgent.agente}</div>
              <div className="text-xs text-[color:var(--text-muted)]">
                Login {editingAgent.agente} | ramal atual {editingAgent.active_extension || editingAgent.ramal || "-"}
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] items-end">
              <label className="space-y-2">
                <span className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Ramal em uso</span>
                <select
                  className="sbc-input"
                  value={editingExtension}
                  onChange={(event) => setEditingExtension(event.target.value)}
                  disabled={queueActionBusy}
                >
                  <option value="">-- selecione o ramal --</option>
                  {(data.extensions || []).map((extension) => (
                    <option key={extension.id || extension.numero} value={extension.numero}>
                      {extension.numero}{extension.nome ? ` - ${extension.nome}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="sbc-btn sbc-btn-primary"
                onClick={saveAgentExtension}
                disabled={queueActionBusy || !editingExtension}
              >
                {queueActionBusy ? <RefreshCw size={14} className="animate-spin" /> : null}
                Salvar ramal
              </button>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Filas do agente</div>
              <div className="text-xs text-[color:var(--text-muted)]">Escolha em quais filas esse agente pode operar.</div>
            </div>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {(data.queues || []).length ? (
                data.queues.map((queue) => {
                  const queueNumber = String(queue.number || "");
                  return (
                    <label key={queue.id || queueNumber} className="flex items-start gap-2 border border-[color:var(--border-default)] rounded-sm px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editingQueues.includes(queueNumber)}
                        onChange={() => toggleQueue(queueNumber)}
                      />
                      <span>
                        <strong>{queue.name || `Fila ${queueNumber}`}</strong>
                        <span className="block text-[11px] text-[color:var(--text-muted)] font-mono">{queueNumber}</span>
                      </span>
                    </label>
                  );
                })
              ) : (
                <div className="text-sm text-[color:var(--text-muted)]">Nenhuma fila disponivel para este cliente.</div>
              )}
            </div>
            {queueActionError ? (
              <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
                ! {queueActionError}
              </div>
            ) : null}
            {queueActionSuccess ? (
              <div className="text-xs text-[color:var(--accent-green)] font-mono border border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.08)] px-3 py-2 rounded-sm">
                {queueActionSuccess}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button type="button" className="sbc-btn sbc-btn-primary" onClick={saveAgentQueues} disabled={queueActionBusy}>
                {queueActionBusy ? <RefreshCw size={14} className="animate-spin" /> : null}
                Salvar filas
              </button>
              <button type="button" className="sbc-btn" onClick={cancelQueueEdit} disabled={queueActionBusy}>
                Fechar
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="sbc-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--border-default)]">
          <h2 className="font-semibold">Relatorios do call center</h2>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            Dados de filas, agentes, atendimento, abandono e nivel de servico.
          </p>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 p-4">
          <div>
            <h3 className="text-sm font-semibold mb-2">Desempenho por fila</h3>
            <div className="overflow-auto">
              <table className="sbc-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Oferecidas</th>
                    <th>Atendidas</th>
                    <th>Abandono</th>
                    <th>SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {(reports.queues || []).map((row) => (
                    <tr key={row.queue}>
                      <td>{row.queue}</td>
                      <td className="font-mono">{row.offered || 0}</td>
                      <td className="font-mono">{row.answered || 0}</td>
                      <td className="font-mono">{row.abandoned || 0}</td>
                      <td className="font-mono">{Math.round(row.service_level || 0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-2">Desempenho por agente</h3>
            <div className="overflow-auto">
              <table className="sbc-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th>Oferecidas</th>
                    <th>Atendidas</th>
                    <th>Abandono</th>
                    <th>SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {(reports.agents || []).map((row) => (
                    <tr key={row.agent}>
                      <td>{row.agent}</td>
                      <td className="font-mono">{row.offered || 0}</td>
                      <td className="font-mono">{row.answered || 0}</td>
                      <td className="font-mono">{row.abandoned || 0}</td>
                      <td className="font-mono">{Math.round(row.service_level || 0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-4 pb-4">
          <div>
            <h3 className="text-sm font-semibold mb-2">Motivos de abandono</h3>
            {(data.abandonment_causes || []).map((row) => (
              <div key={row.name} className="flex justify-between text-xs py-1 border-b border-[color:var(--border-default)]">
                <span>{translateAbandonmentCause(row.name)}</span>
                <strong className="font-mono">{row.value}</strong>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-2">Motivos de desconexao</h3>
            {(data.disconnect_causes || []).map((row) => (
              <div key={row.name} className="flex justify-between text-xs py-1 border-b border-[color:var(--border-default)]">
                <span>{translateHangupCause(row.name)}</span>
                <strong className="font-mono">{row.value}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
